import { APIClient } from './httpClient';
import { GTMData } from '../types/gtm';

export interface GTMConfig {
  gtmId: string;
  enabled: boolean;
}

export class GTMService {
  private gtmId: string | null = null;
  private isLoaded = false;
  private isInitialized = false;

  async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    try {
      console.info('Loading GTM configuration...');
      // Fetch GTM ID from your backend
      const response = await APIClient.get('/gtm/config');

      if (response.success && response.data.gtmId && response.data.enabled) {
        this.gtmId = response.data.gtmId;
        console.info(`Loaded GTM ID: ${this.gtmId}`);
        await this.loadGTM();
      } else {
        console.info('GTM is disabled or no GTM ID provided');
      }
    } catch (error) {
      console.warn('Failed to load GTM configuration:', error);
    }

    this.isInitialized = true;
  }

  private async loadGTM(): Promise<void> {
    if (!this.gtmId || this.isLoaded) {
      return;
    }

    try {
      // Initialize dataLayer first
      this.initializeDataLayer();

      // Inject GTM script into head
      this.injectGTMScript();

      // Inject GTM noscript into body
      this.injectGTMNoScript();

      this.isLoaded = true;
      console.log(`GTM loaded successfully with ID: ${this.gtmId}`);
    } catch (error) {
      console.error('Failed to load GTM:', error);
    }
  }

  private initializeDataLayer(): void {
    // Initialize dataLayer if it doesn't exist
    if (!(window as any).dataLayer) {
      (window as any).dataLayer = [];
    }
  }

  private injectGTMScript(): void {
    // Create and inject the GTM script
    const script = document.createElement('script');
    script.innerHTML = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${this.gtmId}');
    `;

    document.head.appendChild(script);
  }

  private injectGTMNoScript(): void {
    // Create and inject the GTM noscript
    const noscript = document.createElement('noscript');
    const iframe = document.createElement('iframe');
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${this.gtmId}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';

    noscript.appendChild(iframe);
    document.body.insertBefore(noscript, document.body.firstChild);
  }

  // Method to push events to GTM
  pushEvent(event: string, data: GTMData = {}): void {
    if (!this.isLoaded || !this.gtmId) {
      console.debug(`GTM not loaded, skipping event: ${event}`);
      return;
    }

    const dataLayer = (window as any).dataLayer;
    if (!dataLayer) {
      console.warn('dataLayer not available');
      return;
    }

    const eventData = {
      event,
      ...data
    };

    dataLayer.push(eventData);
  }

  // Getter for status checking
  get isGTMLoaded(): boolean {
    return this.isLoaded;
  }
}

export const gtmService = new GTMService();